import dynamic from 'next/dynamic';
import type { ReactElement } from 'react';
import React, { useEffect } from 'react';

import CustomMetatag from '@/components/layouts/custom-metatag';
import Layout from '@/components/layouts/lcv';
import Detail, { OfferDetailProps } from '@/components/sections/offer/detail';
import { IOffersCTA } from '@/components/sections/shared/offers-cta';
import { COUPON_CAMPAIGN_ID } from '@/const/form';
import { MENU_LCV_SPECIAL_OFFER } from '@/contents/lcv/link';
import {
  getActiveOffers,
  getLCVProvince,
  getOfferByURL,
  getReleatedOffer,
} from '@/services/contentstack';
import { trackFBLeadFinancialInitiated } from '@/tag-manager';

const OffersCTA = dynamic(
  () => import('@/components/sections/shared/offers-cta'),
  {
    ssr: false,
  }
);
const FooterLCVGroup = dynamic(
  () => import('@/components/layouts/lcv/footer-group'),
  {
    ssr: false,
  }
);

export async function getStaticPaths() {
  const res = await getActiveOffers('lcv');

  const entries = res.data.entries;

  const paths = entries.map((entry) => ({
    params: {
      url: entry.url,
      category: entry.category[0].url,
    },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }) {
  const res = await getOfferByURL(params.url, 'lcv');
  const resRelated = await getReleatedOffer(
    params.url,
    params.category,
    'lcv',
    8
  );

  res.data.entries = res.data.entries.map((o) => ({
    metatag: {
      title: o.metatag?.title,
      description: o.metatag?.description,
      img: {
        url: o.metatag?.img?.url,
      },
    },
    category: o.category?.map((c) => ({
      name: c.name,
      url: c.url,
    })),
    image_detail: {
      url: o.image_detail?.url,
      dimension: {
        width: o.image_detail?.dimension?.width,
        height: o.image_detail?.dimension?.height,
      },
    },
    begin_date: o.begin_date,
    end_date: o.end_date,
    detail: o.detail,
    remark: o.remark,
    dealer_search: o.dealer_search
      ? {
          is_active: o.dealer_search?.is_active,
          title: o.dealer_search?.title,
        }
      : null,
    buttons: o.buttons.map((c) => ({
      button: { ...c.button },
    })),
    form: o.form,
    name: o.name,
    title: o.title,
    uid: o.uid,
    _version: o._version,
    active: o.active,
    type: o.type,
    model: o?.model,
  }));

  const entry = res.data.entries[0];
  if (!entry) {
    return {
      redirect: {
        destination: `/specialoffer/${params.category}`,
      },
    };
  }

  let provinces: any[] = [];

  if (entry.form) {
    const provincesRes = await getLCVProvince();
    provinces = provincesRes.data.entries;
  }

  let serviceProvinces: any[] = [];

  if (entry.dealer_search?.is_active) {
    const provincesRes = await getLCVProvince(params.category);
    serviceProvinces = provincesRes.data.entries;
  }

  resRelated.data.entries = resRelated.data.entries.map((o) => ({
    name: o.name,
    img: {
      url: o.img && o.img?.url,
    },
    category: o.category?.map((c) => ({
      url: c.url,
    })),
    url: o.url,
    model: o?.model,
  }));

  return {
    props: JSON.parse(
      JSON.stringify({
        entry,
        relatedEntries: resRelated.data.entries,
        provinces,
        serviceProvinces,
      })
    ),
  };
}
export default function Page(props) {
  const { entry, relatedEntries, provinces, serviceProvinces } = props;
  const metatag = entry?.metatag || {};

  const data: OfferDetailProps = {
    category: {
      name: entry?.category?.[0]?.name,
      url: entry?.category?.[0]?.url,
    },
    // image: {
    //   src: entry.img && entry.img?.url,
    //   alt: entry.title,
    //   width: entry.img?.dimension && entry.img?.dimension?.width,
    //   height: entry.img?.dimension && entry.img?.dimension?.height,
    // },
    image_detail: {
      src: entry?.image_detail?.url,
      alt: entry?.title,
      width: entry?.image_detail?.dimension?.width,
      height: entry?.image_detail?.dimension?.height,
    },
    title: entry?.name,
    beginDate: entry?.uid === COUPON_CAMPAIGN_ID ? '' : entry?.begin_date,
    endDate: entry?.uid === COUPON_CAMPAIGN_ID ? '' : entry?.end_date,
    detail: entry?.detail,
    remark: entry?.remark,
    dealerSearch: {
      isActive: entry?.dealer_search?.is_active,
      title: entry?.dealer_search?.title ?? '',
    },
    buttons: entry?.buttons
      ? entry?.buttons?.map((o) => ({ ...o.button }))
      : [],
    form: entry?.form,
    provinces: provinces,
    serviceProvinces: serviceProvinces,
    campaignName: entry?.name,
    campaignId: entry?.uid,
    campaignVersion: entry?._version,
    lcvModel: entry?.type,
    fbModel: entry?.model,
    vehicleType: 'lcv',
  };

  const offerData: IOffersCTA = {
    title: 'ข้อเสนออื่นๆ',
    background: 'gray',
    loop: false,
    items: relatedEntries?.map((o) => ({
      image: {
        src: o.img ? o.img?.url : '',
        alt: o.name,
      },
      title: o.name,
      button: {
        url: `${MENU_LCV_SPECIAL_OFFER.url}/${o.category[0].url}/${o.url}`,
        label: 'ดูรายละเอียด',
        icon: 'arrow-forward',
      },
      model: o.model,
    })),
  };

  let metaImg = '';
  if (metatag?.img?.url) {
    metaImg = metatag?.img?.url;
  } else if (entry?.image_detail?.url) {
    metaImg = entry?.image_detail?.url;
  }

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    trackFBLeadFinancialInitiated({ content_ids: data.fbModel });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <CustomMetatag
        title={metatag?.title}
        description={metatag?.description}
        imgURL={metaImg != '' ? metaImg : undefined}
      />
      <main>
        <Detail {...data} />
        {!!offerData.items && offerData.items.length > 1 && (
          <OffersCTA {...offerData} />
        )}
      </main>
      <FooterLCVGroup />
    </>
  );
}

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
